import { Link } from "react-router-dom";
import { Facebook, Mail, MapPin, Phone, User } from "lucide-react";
import { Logo } from "./Logo";
import { BUSINESS } from "../lib/site";

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/equipment", label: "Equipment We Buy" },
  { to: "/electrical-wire-cable", label: "Electrical Wire" },
  { to: "/sell-your-surplus", label: "Sell Your Surplus" },
  { to: "/contact", label: "Contact Us" },
  { to: "/privacy", label: "Privacy Policy" },
];

const BUY_LINKS = [
  { to: "/circuit-breakers", label: "Circuit Breakers" },
  { to: "/bus-plugs-disconnects", label: "Bus Plugs & Disconnects" },
  { to: "/transformers-switchgear", label: "Transformers & Switchgear" },
  { to: "/electrical-wire-cable", label: "Electrical Wire & Cable" },
  { to: "/industrial-surplus", label: "Industrial Surplus" },
  { to: "/warehouse-liquidations", label: "Warehouse Liquidations" },
];

export const Footer = () => (
  <footer className="bg-ves-charcoal pb-20 md:pb-0" data-testid="site-footer">
    <div className="h-1.5 hazard-stripes" aria-hidden="true" />
    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
      <div>
        <Logo stacked />
        <p className="mt-4 text-sm leading-relaxed text-white/70">
          Nationwide electrical surplus &amp; industrial equipment purchasing.
          New, used, obsolete and surplus — from single pallets to complete
          warehouse inventories.
        </p>
        <a
          href={BUSINESS.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:border-ves-green hover:text-ves-green"
          data-testid="footer-facebook-link"
        >
          <Facebook className="h-4 w-4" /> Follow us on Facebook
        </a>
      </div>

      <nav aria-label="Quick links">
        <h3 className="font-heading text-sm font-black tracking-widest text-ves-green">
          QUICK LINKS
        </h3>
        <ul className="mt-4 space-y-2.5">
          {QUICK_LINKS.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                className="text-sm text-white/75 transition-colors duration-150 hover:text-ves-green"
                data-testid={`footer-link-${l.to.replace(/\//g, "") || "home"}`}
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <nav aria-label="Equipment we buy">
        <h3 className="font-heading text-sm font-black tracking-widest text-ves-green">
          WHAT WE BUY
        </h3>
        <ul className="mt-4 space-y-2.5">
          {BUY_LINKS.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                className="text-sm text-white/75 transition-colors duration-150 hover:text-ves-green"
                data-testid={`footer-buy-${l.to.replace(/\//g, "")}`}
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div>
        <h3 className="font-heading text-sm font-black tracking-widest text-ves-green">
          CONTACT
        </h3>
        <ul className="mt-4 space-y-3 text-sm text-white/75">
          <li className="flex items-start gap-2">
            <User className="mt-0.5 h-4 w-4 shrink-0 text-ves-green" />
            <span>Purchasing Manager: {BUSINESS.manager}</span>
          </li>
          <li>
            <a
              href={BUSINESS.phoneTel}
              className="flex items-center gap-2 font-semibold text-white transition-colors duration-150 hover:text-ves-green"
              data-testid="footer-phone-link"
            >
              <Phone className="h-4 w-4 shrink-0 text-ves-green" />
              {BUSINESS.phoneDisplay}
            </a>
          </li>
          <li>
            <a
              href={BUSINESS.emailHref}
              className="flex items-center gap-2 break-all transition-colors duration-150 hover:text-ves-green"
              data-testid="footer-email-link"
            >
              <Mail className="h-4 w-4 shrink-0 text-ves-green" />
              {BUSINESS.email}
            </a>
          </li>
          <li>
            <a
              href={BUSINESS.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-2 transition-colors duration-150 hover:text-ves-green"
              data-testid="footer-address-link"
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ves-green" />
              <span>{BUSINESS.address}</span>
            </a>
          </li>
        </ul>
        <p className="mt-4 text-xs font-semibold tracking-wide text-white/50">
          Based in Michigan — Buying Nationwide
        </p>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-center text-xs text-white/50 sm:px-6 md:flex-row md:text-left lg:px-8">
        <span>
          © {new Date().getFullYear()} {BUSINESS.nameUpper}. All rights reserved.
        </span>
        <span className="font-heading font-bold tracking-wide text-ves-green">
          {BUSINESS.slogan}
        </span>
      </div>
    </div>
  </footer>
);
